/**
 * Audio preferences persistence
 * Extends the pattern from storage.ts
 */

import type { AudioPreferences } from '../types/audio';
import { SoundCategory } from '../types/audio';

// Storage keys for audio preferences
const AUDIO_STORAGE_KEYS = {
  MASTER_VOLUME: 'blather_audio_master_volume',
  IS_MUTED: 'blather_audio_muted',
  CATEGORY_VOLUMES: 'blather_audio_category_volumes',
} as const;

/**
 * Default audio preferences
 */
export const DEFAULT_AUDIO_PREFS: AudioPreferences = {
  masterVolume: 0.7,
  isMuted: false,
  categoryVolumes: {
    [SoundCategory.MUSIC]: 0.6,
    [SoundCategory.SFX]: 0.8,
    [SoundCategory.AMBIENT]: 0.5,
    [SoundCategory.UI]: 0.7,
    [SoundCategory.VOICE]: 1.0,
  },
};

/**
 * Load audio preferences from localStorage
 */
export function loadAudioPreferences(): AudioPreferences {
  try {
    const masterVolume = localStorage.getItem(AUDIO_STORAGE_KEYS.MASTER_VOLUME);
    const isMuted = localStorage.getItem(AUDIO_STORAGE_KEYS.IS_MUTED);
    const categoryVolumes = localStorage.getItem(AUDIO_STORAGE_KEYS.CATEGORY_VOLUMES);

    return {
      masterVolume: masterVolume ? parseFloat(masterVolume) : DEFAULT_AUDIO_PREFS.masterVolume,
      isMuted: isMuted ? isMuted === 'true' : DEFAULT_AUDIO_PREFS.isMuted,
      categoryVolumes: categoryVolumes
        ? JSON.parse(categoryVolumes)
        : DEFAULT_AUDIO_PREFS.categoryVolumes,
    };
  } catch (error) {
    console.error('Failed to load audio preferences:', error);
    return DEFAULT_AUDIO_PREFS;
  }
}

/**
 * Save audio preferences to localStorage
 */
export function saveAudioPreferences(prefs: AudioPreferences): void {
  try {
    localStorage.setItem(AUDIO_STORAGE_KEYS.MASTER_VOLUME, prefs.masterVolume.toString());
    localStorage.setItem(AUDIO_STORAGE_KEYS.IS_MUTED, prefs.isMuted.toString());
    localStorage.setItem(AUDIO_STORAGE_KEYS.CATEGORY_VOLUMES, JSON.stringify(prefs.categoryVolumes));
  } catch (error) {
    console.error('Failed to save audio preferences:', error);
  }
}

/**
 * Reset audio preferences to defaults
 */
export function resetAudioPreferences(): void {
  saveAudioPreferences(DEFAULT_AUDIO_PREFS);
}
